import * as THREE from 'three';
import { state, frameToSeconds, clipDurationSeconds } from '../state.ts';
import type { Viewport } from '../scene/viewport.ts';
import type { RenderLoop } from '../scene/renderLoop.ts';
import type { CameraDirector } from '../camera/director.ts';
import type { ContactLimb, ContactPin, ContactTarget, InfluenceKey } from '../retarget/contacts.ts';
import type { FingerTune } from '../retarget/fingerTune.ts';

/**
 * The agent tooling API ("look, judge, adjust" — see docs/AUTO_CLEANUP.md).
 * A JSON-in/JSON-out facade over the SAME action functions the panels use, so
 * anything an agent does is exactly what a human could have done — keyable,
 * serializable, undoable. Exposed as `window.aka` in dev and consumed by the
 * in-app assistant.
 */

// --- Snapshot shapes ---------------------------------------------------------------

export interface ActorSnapshot {
  id: string;
  label: string;
  kind: 'skeleton' | 'prop';
  visible: boolean;
  avatar: string | null;
  hasPuppet: boolean;
  adjust: { offset: number[]; scale: number };
  adjustKeys: { time: number; offset: number[]; scale: number }[];
  fingerTune: FingerTune;
  fingerTuneKeys: { time: number; tune: FingerTune }[];
}

export interface SceneSnapshot {
  take: {
    name: string | null;
    fps: number;
    totalFrames: number;
    durationSeconds: number;
    marks: { start: number; end: number };
    playhead: number;
    viewMode: string;
  };
  actors: ActorSnapshot[];
  contacts: ContactPin[];
  fingerFix: number;
  cameras: { count: number; shots: number; view: string };
}

export interface LookOptions {
  /** Frame to pose (defaults to the playhead). */
  frame?: number;
  /** Output size (defaults 768×512 — small enough for model context). */
  width?: number;
  height?: number;
  /** Frame a bone closeup instead of using the current camera. */
  focus?: {
    actorId: string;
    bone: string; // canonical: 'lefthand', 'head', 'hips', …
    /** Metres from the bone (default 0.6). */
    distance?: number;
    /** Degrees around the subject (default 30) and above it (default 15). */
    azimuth?: number;
    elevation?: number;
  };
  /** Look through a camera id, or an explicit pose. */
  camera?: string | { position: number[]; target: number[]; fov?: number };
}

export interface AgentApiDeps {
  viewport: Viewport;
  loop: RenderLoop;
  director: CameraDirector;
  setMixersTime: (seconds: number) => void;
  tickActors: () => void;
  /** Resolve an actor's bone for a canonical slot (puppet in mesh view, else source). */
  actorBone: (actorId: string, canonical: string) => THREE.Object3D | null;
  buildSnapshot: () => SceneSnapshot;
  /** In-memory settings snapshot/restore (no asset reloads). */
  captureSettings: () => unknown;
  restoreSettings: (data: unknown) => void;
  // Actions (the same functions the panels call):
  scrub: (frame: number) => void;
  setMarks: (start: number, end: number) => void;
  setFingerTune: (actorId: string, patch: Partial<FingerTune>) => void;
  keyFingerTune: (actorId: string, seconds: number) => void;
  deleteFingerTuneKey: (actorId: string, index: number) => void;
  setActorAdjust: (
    actorId: string,
    patch: { x?: number; y?: number; z?: number; scale?: number }
  ) => void;
  keyActorOffset: (actorId: string, seconds: number) => void;
  deleteAdjustKey: (actorId: string, index: number) => void;
  setFingerFix: (strength: number) => void;
  addPin: (spec: {
    actorId: string;
    limb: ContactLimb;
    target: ContactTarget;
    keys?: InfluenceKey[];
    enabled?: boolean;
  }) => string;
  updatePin: (pinId: string, patch: Record<string, unknown>) => void;
  setPinKey: (pinId: string, seconds: number, value: number) => void;
  deletePin: (pinId: string) => void;
  analyze?: (opts?: { startFrame?: number; endFrame?: number }) => unknown;
}

export interface AgentApi {
  inspect: () => SceneSnapshot;
  look: (opts?: LookOptions) => string;
  lookRange: (frames: number[], opts?: Omit<LookOptions, 'frame'>) => string[];
  boneWorld: (
    actorId: string,
    bone: string,
    frame?: number
  ) => { position: number[]; quaternion: number[] } | null;
  distance: (
    a: { actorId: string; bone: string },
    b: { actorId: string; bone: string },
    frame?: number
  ) => number | null;
  analyze: (opts?: { startFrame?: number; endFrame?: number }) => unknown;
  // Actions — every mutation returns the fresh snapshot so agents see effects.
  scrub: (frame: number) => SceneSnapshot;
  setMarks: (start: number, end: number) => SceneSnapshot;
  setFingerTune: (actorId: string, patch: Partial<FingerTune>) => SceneSnapshot;
  keyFingerTune: (actorId: string, frame: number, patch?: Partial<FingerTune>) => SceneSnapshot;
  deleteFingerTuneKey: (actorId: string, index: number) => SceneSnapshot;
  setActorAdjust: (
    actorId: string,
    patch: { x?: number; y?: number; z?: number; scale?: number }
  ) => SceneSnapshot;
  keyActorOffset: (
    actorId: string,
    frame: number,
    patch?: { x?: number; y?: number; z?: number; scale?: number }
  ) => SceneSnapshot;
  deleteAdjustKey: (actorId: string, index: number) => SceneSnapshot;
  setFingerFix: (strength: number) => SceneSnapshot;
  addPin: (spec: {
    actorId: string;
    limb: ContactLimb;
    target: ContactTarget;
    keys?: InfluenceKey[];
    enabled?: boolean;
  }) => { pinId: string; snapshot: SceneSnapshot };
  updatePin: (pinId: string, patch: Record<string, unknown>) => SceneSnapshot;
  setPinKey: (pinId: string, frame: number, value: number) => SceneSnapshot;
  deletePin: (pinId: string) => SceneSnapshot;
  checkpoint: (label?: string) => string;
  restore: (id: string) => SceneSnapshot;
  listCheckpoints: () => { id: string; label: string }[];
}

const DEG2RAD = Math.PI / 180;

export function createAgentApi(deps: AgentApiDeps): AgentApi {
  const checkpoints = new Map<string, { label: string; data: unknown }>();
  let checkpointCounter = 0;

  const snap = (): SceneSnapshot => deps.buildSnapshot();

  /**
   * Deterministic frame render. Poses the requested frame, hides all edit viz
   * (via the exporting flag the viz modules already honour), renders at a fixed
   * size and restores everything — the live viewport never visibly changes.
   */
  function look(opts: LookOptions = {}): string {
    const frame = Math.min(Math.max(opts.frame ?? state.currentFrame, 0), state.totalFrames);
    const width = opts.width ?? 768;
    const height = opts.height ?? 512;
    const cam = deps.viewport.camera;
    const saved = {
      pos: cam.position.clone(),
      quat: cam.quaternion.clone(),
      fov: cam.fov,
      exporting: state.exporting,
    };

    deps.loop.stop();
    state.exporting = true; // hides gizmos/paths in the viz modules
    const restoreExport = deps.viewport.beginExport(width, height);
    try {
      deps.setMixersTime(frame / state.fps);
      deps.tickActors();

      if (opts.focus) {
        const bone = deps.actorBone(opts.focus.actorId, opts.focus.bone);
        if (bone) {
          bone.updateWorldMatrix(true, false);
          const target = new THREE.Vector3().setFromMatrixPosition(bone.matrixWorld);
          const d = opts.focus.distance ?? 0.6;
          const az = (opts.focus.azimuth ?? 30) * DEG2RAD;
          const el = (opts.focus.elevation ?? 15) * DEG2RAD;
          cam.position.set(
            target.x + d * Math.cos(el) * Math.sin(az),
            target.y + d * Math.sin(el),
            target.z + d * Math.cos(el) * Math.cos(az)
          );
          cam.lookAt(target);
        }
      } else if (typeof opts.camera === 'string') {
        const sample = deps.director.sample(opts.camera, frame / state.fps);
        if (sample) {
          cam.position.copy(sample.position);
          cam.lookAt(sample.target);
          cam.fov = sample.fov;
          cam.updateProjectionMatrix();
        }
      } else if (opts.camera) {
        cam.position.fromArray(opts.camera.position);
        cam.lookAt(new THREE.Vector3().fromArray(opts.camera.target));
        if (opts.camera.fov) {
          cam.fov = opts.camera.fov;
          cam.updateProjectionMatrix();
        }
      }

      deps.viewport.render();
      return deps.viewport.renderer.domElement.toDataURL('image/png');
    } finally {
      restoreExport();
      cam.position.copy(saved.pos);
      cam.quaternion.copy(saved.quat);
      if (cam.fov !== saved.fov) {
        cam.fov = saved.fov;
        cam.updateProjectionMatrix();
      }
      state.exporting = saved.exporting;
      deps.setMixersTime(state.currentFrame / state.fps);
      deps.tickActors();
      deps.loop.renderOnce();
      deps.loop.start();
    }
  }

  function poseProbe<T>(frame: number | undefined, fn: () => T): T {
    if (frame === undefined) return fn();
    const f = Math.min(Math.max(frame, 0), state.totalFrames);
    deps.loop.stop();
    try {
      deps.setMixersTime(f / state.fps);
      deps.tickActors();
      return fn();
    } finally {
      deps.setMixersTime(state.currentFrame / state.fps);
      deps.tickActors();
      deps.loop.start();
    }
  }

  return {
    inspect: snap,
    look,
    lookRange: (frames, opts = {}) => frames.slice(0, 12).map((frame) => look({ ...opts, frame })),

    boneWorld: (actorId, bone, frame) =>
      poseProbe(frame, () => {
        const b = deps.actorBone(actorId, bone);
        if (!b) return null;
        b.updateWorldMatrix(true, false);
        const p = new THREE.Vector3().setFromMatrixPosition(b.matrixWorld);
        const q = new THREE.Quaternion();
        b.getWorldQuaternion(q);
        return {
          position: p.toArray().map((n) => +n.toFixed(4)),
          quaternion: q.toArray().map((n) => +n.toFixed(4)),
        };
      }),

    distance: (a, b, frame) =>
      poseProbe(frame, () => {
        const ba = deps.actorBone(a.actorId, a.bone);
        const bb = deps.actorBone(b.actorId, b.bone);
        if (!ba || !bb) return null;
        ba.updateWorldMatrix(true, false);
        bb.updateWorldMatrix(true, false);
        const pa = new THREE.Vector3().setFromMatrixPosition(ba.matrixWorld);
        const pb = new THREE.Vector3().setFromMatrixPosition(bb.matrixWorld);
        return +pa.distanceTo(pb).toFixed(4);
      }),

    analyze: (opts) => (deps.analyze ? deps.analyze(opts) : { error: 'analyze unavailable' }),

    scrub: (frame) => {
      deps.scrub(frame);
      return snap();
    },
    setMarks: (start, end) => {
      deps.setMarks(start, end);
      return snap();
    },
    setFingerTune: (actorId, patch) => {
      deps.setFingerTune(actorId, patch);
      return snap();
    },
    keyFingerTune: (actorId, frame, patch) => {
      if (patch) deps.setFingerTune(actorId, patch);
      deps.keyFingerTune(actorId, frameToSeconds(frame));
      return snap();
    },
    deleteFingerTuneKey: (actorId, index) => {
      deps.deleteFingerTuneKey(actorId, index);
      return snap();
    },
    setActorAdjust: (actorId, patch) => {
      deps.setActorAdjust(actorId, patch);
      return snap();
    },
    keyActorOffset: (actorId, frame, patch) => {
      if (patch) deps.setActorAdjust(actorId, patch);
      deps.keyActorOffset(actorId, frameToSeconds(frame));
      return snap();
    },
    deleteAdjustKey: (actorId, index) => {
      deps.deleteAdjustKey(actorId, index);
      return snap();
    },
    setFingerFix: (strength) => {
      deps.setFingerFix(Math.min(Math.max(strength, 0), 1));
      return snap();
    },
    addPin: (spec) => ({ pinId: deps.addPin(spec), snapshot: snap() }),
    updatePin: (pinId, patch) => {
      deps.updatePin(pinId, patch);
      return snap();
    },
    setPinKey: (pinId, frame, value) => {
      deps.setPinKey(pinId, frameToSeconds(frame), Math.min(Math.max(value, 0), 1));
      return snap();
    },
    deletePin: (pinId) => {
      deps.deletePin(pinId);
      return snap();
    },

    checkpoint: (label = '') => {
      const id = `cp-${++checkpointCounter}`;
      checkpoints.set(id, { label: label || `checkpoint ${checkpointCounter}`, data: deps.captureSettings() });
      return id;
    },
    restore: (id) => {
      const cp = checkpoints.get(id);
      if (cp) deps.restoreSettings(cp.data);
      return snap();
    },
    listCheckpoints: () => [...checkpoints.entries()].map(([id, cp]) => ({ id, label: cp.label })),
  };
}

/** Duration in seconds of the currently loaded clip (probe convenience). */
export function clipSeconds(): number {
  return clipDurationSeconds();
}
